import {
  useGetSubscription,
  useListBillingPlans,
  useListPaymentMethods,
  useUpdateSubscription,
} from "@/api/stripe";
import { FreeTrialBanner } from "@/components/billing/FreeTrialBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PLANS } from "@/constants/plans";
import { ROUTES } from "@/constants/routes";
import { useIsSelfServeBilling } from "@/hooks/useIsSelfServeBilling";
import * as analytics from "@/utils/analytics";
import { formatCurrency } from "@/utils/currency";
import {
  buildPlanChangeAction,
  transformBillingPlans,
  type PlanSelection,
  type TransformedPlan,
} from "@/utils/planChange";
import { route } from "@/utils/route";
import { ErrorDisplay, Spinner } from "@cohesive/ui-components";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { PlanConfigurationModal } from "./PlanConfigurationModal";

/** The plan vocabulary lives with the plan-change logic. */
export type { PlanSelection, TransformedPlan } from "@/utils/planChange";

export function PlansPage() {
  const navigate = useNavigate();
  const { data: subscription } = useGetSubscription();
  const { data: paymentMethods } = useListPaymentMethods();
  const updateSubscription = useUpdateSubscription();
  const { data: billingPlans, isLoading, error } = useListBillingPlans();
  const isSelfServeBilling = useIsSelfServeBilling();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<TransformedPlan | null>(
    null,
  );

  // Transform API data to group monthly/yearly plans
  const transformedPlans = useMemo(
    () => transformBillingPlans(billingPlans?.data.billing_plans),
    [billingPlans],
  );

  // Helper function to get the displayed price per month for a plan
  const getDisplayedPrice = (plan: TransformedPlan): number => {
    if (plan.yearly_price !== undefined) {
      return plan.yearly_price / 12; // Show yearly price divided by 12
    } else if (plan.monthly_price !== undefined) {
      return plan.monthly_price; // Show monthly price
    }
    return 0; // Fallback, should not happen
  };

  const handlePlanSelect = (plan: TransformedPlan) => {
    analytics.trackBillingPlanSelected({
      planName: plan.name,
      source: "plans_page",
    });
    setSelectedPlan(plan);
    setShowConfigModal(true);
  };

  // Check if user has payment method for plan configuration
  const hasPaymentMethod =
    paymentMethods?.data.payment_methods?.some((pm) => pm.default) || false;

  const handleConfigurationComplete = async (selection: PlanSelection) => {
    analytics.trackBillingPlanConfigured({
      planName: selection.plan.name,
      billingInterval: selection.billing_cycle,
      seatCount: selection.user_count,
      hasPaymentMethod,
    });

    setShowConfigModal(false);

    const planChange = buildPlanChangeAction({
      selection,
      subscription: subscription?.data.stripe_subscription,
      paymentMethods: paymentMethods?.data.payment_methods,
    });

    if (planChange.type === "update") {
      updateSubscription
        .mutateAsync(planChange.payload)
        .then(() => {
          toast.success("Plan updated successfully");
          navigate(ROUTES.HOME);
        })
        .catch((error) => {
          toast.error(error.message);
        });
    } else {
      const checkoutParams: Record<string, string> = {
        plan: selection.plan.name,
        billing_cycle: selection.billing_cycle,
        user_count: String(selection.user_count),
      };

      if (selection.redirectTo) {
        checkoutParams.redirect_to = selection.redirectTo;
      }

      navigate(route(ROUTES.BILLING.CHECKOUT, {}, checkoutParams));
    }
  };

  const standardPlan = transformedPlans.find(
    (plan) => plan.name === PLANS.STANDARD,
  );

  const proPlan = transformedPlans.find((plan) => plan.name === PLANS.PRO);

  // Get current plan from subscription
  const currentPlanName = subscription?.data.stripe_subscription?.plan_name;
  const subscriptionStatus = subscription?.data.stripe_subscription?.status;
  const isCurrentPlan = (planName: string) => currentPlanName === planName;

  // Check if subscription is cancelled - cancelled users can downgrade
  const isSubscriptionCancelled = subscriptionStatus === "canceled";

  // Helper to determine if plan should show as "current" (active subscription, not cancelled)
  const isActivePlan = (planName: string) =>
    isCurrentPlan(planName) &&
    ["active", "trialing"].includes(subscriptionStatus || "");

  // Calculate number of visible plans to determine page text
  // Pro users can only see Standard if their subscription is cancelled
  const canSeeStandardPlan =
    standardPlan && (currentPlanName !== PLANS.PRO || isSubscriptionCancelled);

  const visiblePlanCount = [
    canSeeStandardPlan, // Standard (hidden if on active Pro)
    proPlan, // Pro (always visible if exists)
    true, // Enterprise (always visible)
  ].filter(Boolean).length;

  const isMultiplePlans = visiblePlanCount > 1;
  const title = isMultiplePlans ? "Choose your Plan" : "Your Plan";

  if (error) {
    return <ErrorDisplay message={error.message} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      <title>{title}</title>
      <div className="max-w-5xl mx-auto min-h-screen flex flex-col justify-center">
        <div className="text-center mb-12">
          <h1
            className={`text-4xl font-bold text-foreground ${
              isMultiplePlans ? "mb-4" : "mb-8"
            }`}
          >
            {title}
          </h1>
          {isMultiplePlans && (
            <p className="text-muted-foreground text-lg mb-8">
              Select the plan that best fits your needs
            </p>
          )}

          {/* Free Trial Alert */}
          <FreeTrialBanner
            subscription={subscription}
            onManageBillingClick={() => {
              const currentPlan = transformedPlans.find(
                (p) => p.name === currentPlanName,
              );
              if (currentPlan) {
                handlePlanSelect(currentPlan);
              }
            }}
            className="max-w-4xl mx-auto mb-8"
          />
        </div>

        <div
          className={`grid gap-8 max-w-6xl mx-auto ${
            !canSeeStandardPlan ? "lg:grid-cols-2" : "lg:grid-cols-3"
          }`}
        >
          {/* Standard Plan - Hidden if user is on active Pro (downgrades allowed if cancelled) */}
          {canSeeStandardPlan && (
            <Card
              data-testid="standard-plan-card"
              className={`border-2 ${isActivePlan(PLANS.STANDARD) ? "border-lc-active" : "border-primary"}`}
            >
              <CardHeader className="text-center pb-8 relative">
                {isActivePlan(PLANS.STANDARD) && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-spectrum-green text-white hover:bg-spectrum-green transition-colors">
                    Current Plan
                  </Badge>
                )}

                <CardTitle className="text-3xl font-bold">Standard</CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold">
                    {standardPlan
                      ? formatCurrency(getDisplayedPrice(standardPlan), {
                          hideCentsForWholeNumbers: true,
                        })
                      : "$0"}
                  </span>
                  <span className="text-muted-foreground ml-2">/ month</span>
                </div>
                <CardDescription className="text-lg mt-2">
                  Great for growing teams
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center">
                  {isSelfServeBilling ? (
                    <Button
                      data-testid="standard-plan-button"
                      onClick={() =>
                        standardPlan && handlePlanSelect(standardPlan)
                      }
                      className="w-full"
                      disabled={!standardPlan || updateSubscription.isPending}
                      variant={
                        isActivePlan(PLANS.STANDARD) ? "secondary" : "default"
                      }
                    >
                      {isActivePlan(PLANS.STANDARD)
                        ? "Configure Plan"
                        : "Get Standard"}
                    </Button>
                  ) : (
                    <div className="w-full py-3 px-4 text-center text-sm text-muted-foreground border border-muted rounded">
                      Contact your organization administrator to modify your
                      Enterprise plan
                    </div>
                  )}
                </div>

                <ul className="space-y-3">
                  {[
                    "All core features",
                    "Standard email support (24-48h response)",
                    "Basic automations and workflows",
                    "Individual workspace management",
                    "Standard integrations",
                    "Community support access",
                    `${standardPlan?.included_credits_per_seat || 1500} credits included per seat`,
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 bg-primary/20 rounded mr-3 mt-0.5 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Pro Plan */}
          {proPlan && (
            <Card
              data-testid="pro-plan-card"
              className={`border-2 ${
                isActivePlan(PLANS.PRO)
                  ? "border-lc-active"
                  : "border-rust dark:border-teal/40"
              }`}
            >
              <CardHeader className="text-center pb-8 relative">
                {isActivePlan(PLANS.PRO) && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-spectrum-green text-white hover:bg-spectrum-green transition-colors">
                    Current Plan
                  </Badge>
                )}

                <CardTitle className="text-3xl font-bold">Pro</CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold">
                    {proPlan
                      ? formatCurrency(getDisplayedPrice(proPlan), {
                          hideCentsForWholeNumbers: true,
                        })
                      : "$0"}
                  </span>
                  <span className="text-muted-foreground ml-2">/ month</span>
                </div>
                <CardDescription className="text-lg mt-2">
                  For power users and large teams
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center">
                  {isSelfServeBilling ? (
                    <Button
                      data-testid="pro-plan-button"
                      onClick={() => proPlan && handlePlanSelect(proPlan)}
                      className={`w-full ${
                        isActivePlan(PLANS.PRO)
                          ? ""
                          : "bg-rust hover:bg-rust text-white"
                      }`}
                      disabled={!proPlan || updateSubscription.isPending}
                      variant={
                        isActivePlan(PLANS.PRO) ? "secondary" : "default"
                      }
                    >
                      {isActivePlan(PLANS.PRO) ? "Configure Plan" : "Get Pro"}
                    </Button>
                  ) : (
                    <div className="w-full py-3 px-4 text-center text-sm text-muted-foreground border border-muted rounded">
                      Contact your organization administrator to modify your
                      Enterprise plan
                    </div>
                  )}
                </div>

                <ul className="space-y-3">
                  {[
                    "All core features",
                    "Standard email support (24-48h response)",
                    "Organizational Administration & Billing",
                    "Advanced automations and workflows",
                    "Team collaboration tools",
                    "Enhanced integrations",
                    "Usage analytics and reporting",
                    `${proPlan?.included_credits_per_seat || 2000} credits included per seat`,
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 bg-spectrum-blue-light/30 dark:bg-spectrum-blue-dark/30 rounded mr-3 mt-0.5 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-teal"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Enterprise Plan */}
          <Card
            data-testid="enterprise-plan-card"
            className="border-2 border-neutral-500 dark:border-neutral-400"
          >
            <CardHeader className="text-center pb-8 relative">
              <CardTitle className="text-3xl font-bold">Enterprise</CardTitle>
              <div className="mt-4">
                <span className="text-5xl font-bold">Custom</span>
                <span className="text-muted-foreground ml-2">pricing</span>
              </div>
              <CardDescription className="text-lg mt-2">
                For large organizations with specific needs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <Button
                  data-testid="enterprise-plan-button"
                  onClick={() => {
                    analytics.trackBillingEnterpriseInquiryClicked({
                      source: "plans_page",
                    });
                    navigate(ROUTES.BILLING.ENTERPRISE_INQUIRY);
                  }}
                  className="w-full bg-neutral-500 hover:bg-neutral-600 text-white"
                >
                  Contact Us
                </Button>
              </div>

              <ul className="space-y-3">
                {[
                  "All Pro features included",
                  "Enterprise-grade security (SSO, SOC2)",
                  "Dedicated Customer Success Manager",
                  "Priority support & SLA guarantees",
                  "Custom integrations & servicing",
                  "Advanced compliance & auditing",
                  "Flexible usage & pricing models",
                  "Custom credit allocations",
                ].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 bg-neutral-100 dark:bg-neutral-900/30 rounded mr-3 mt-0.5 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-neutral-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Plan Configuration Modal */}
      {selectedPlan && (
        <PlanConfigurationModal
          plan={selectedPlan}
          open={showConfigModal}
          buttonLoading={updateSubscription.isPending}
          onComplete={handleConfigurationComplete}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </>
  );
}
